import { useRef, useState, useCallback, useMemo } from 'react';
import { useNFTStore, useUIStore } from '../../stores';
import { Modal } from '../common/Modal';
import { Button } from '../common/Button';
import { Input } from '../common/Input';
import { ImagePreviewModal } from '../common/ImagePreviewModal';
import { BannerCropCanvas, isFillLayerByDefault } from './BannerCropCanvas';
import { useCropInteraction } from '../../hooks';
import { preloadLayerImages, getDecodedImageFromCache } from '../../hooks/useLayerImage';
import { fetchNFTByNumber } from '../../services/solana';
import { BANNER_PRESETS, BACKGROUND_PRESETS, type BackgroundPreset } from '../../constants';
import { downloadImage, isBackgroundLayer, isCharacterLayer, isFrameLayer } from '../../utils';
import type { BannerPreset, BannerCropNFTElement, NFTData, NFTLayerAsset } from '../../types';

const MAX_VIEWPORT_WIDTH = 600;
const MAX_VIEWPORT_HEIGHT = 400;
const PRIMARY_NFT_ID = 'primary';

// Helper to extract character-only layers (no background/frame)
function getCharacterOnlyLayers(layerAssets: NFTLayerAsset[]): NFTLayerAsset[] {
  return layerAssets.filter(isCharacterLayer);
}

export function BannerCropModal() {
  const { currentNFT, fetchedNFTs } = useNFTStore();
  const { activeModal, closeModal } = useUIStore();
  const [isExporting, setIsExporting] = useState(false);
  const [selectedPreset] = useState<BannerPreset>(BANNER_PRESETS[0]);
  // Fixed layer IDs can be user-toggled in future UI enhancement
  const [fixedLayerIds] = useState<Set<string>>(new Set());
  // Background selection state
  const [selectedBackground, setSelectedBackground] = useState<BackgroundPreset>(BACKGROUND_PRESETS[0]);
  const [customBackgroundUrl, setCustomBackgroundUrl] = useState<string | null>(null);
  const backgroundInputRef = useRef<HTMLInputElement>(null);
  // Vertical offset for background and frame layers (percentage of overflow)
  const [backgroundVerticalOffset, setBackgroundVerticalOffset] = useState(0);
  const [frameVerticalOffset, setFrameVerticalOffset] = useState(0);
  // Snap to grid for character positioning
  const [snapEnabled, setSnapEnabled] = useState(true);

  // Additional NFT characters state
  const [additionalNFTs, setAdditionalNFTs] = useState<BannerCropNFTElement[]>([]);
  const [selectedNFTId, setSelectedNFTId] = useState<string | null>(null);

  // NFT search state
  const [showNFTSearch, setShowNFTSearch] = useState(false);
  const [nftSearchValue, setNftSearchValue] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [previewNFT, setPreviewNFT] = useState<NFTData | null>(null);

  // Preview modal state for Telegram fallback
  const [previewImage, setPreviewImage] = useState<{ url: string; filename: string } | null>(null);

  // Calculate viewport dimensions that fit within max bounds
  const aspectRatio = selectedPreset.width / selectedPreset.height;
  let viewportWidth = MAX_VIEWPORT_WIDTH;
  let viewportHeight = MAX_VIEWPORT_WIDTH / aspectRatio;

  if (viewportHeight > MAX_VIEWPORT_HEIGHT) {
    viewportHeight = MAX_VIEWPORT_HEIGHT;
    viewportWidth = MAX_VIEWPORT_HEIGHT * aspectRatio;
  }

  const containerRef = useRef<HTMLDivElement>(null);
  // Only enable primary NFT dragging when it's selected (or nothing is selected for backwards compat)
  const isPrimaryDragEnabled = selectedNFTId === null || selectedNFTId === PRIMARY_NFT_ID;
  const { offset, scale, minScale, setScale, setImageBounds, reset, isDragging, snapGuides } = useCropInteraction(
    containerRef,
    {
      maxScale: 3,
      initialScale: 1,
      canvasSize: viewportHeight, // Fallback for square mode
      canvasWidth: viewportWidth, // Banner viewport width for horizontal movement
      canvasHeight: viewportHeight, // Banner viewport height for vertical movement
      horizontalOnly: false, // Allow both horizontal and vertical panning for banner
      snapToCenter: snapEnabled,
      snapThreshold: 12, // Pixels from center to trigger snap
      disableWheelZoom: true, // Use slider only for zoom
      disabled: !isPrimaryDragEnabled, // Disable when an additional NFT is selected
    }
  );

  const isOpen = activeModal === 'bannerCrop';
  const imageUrl = currentNFT?.imageUrl || '';
  const layerAssets = currentNFT?.layerAssets || [];
  const useLayerMode = layerAssets.length > 0;

  // Build effective fixed layer IDs - combine user toggles with auto-detection
  const effectiveFixedLayerIds = useMemo(() => {
    if (fixedLayerIds.size > 0) {
      return fixedLayerIds;
    }
    // Auto-detect based on naming
    const autoFixed = new Set<string>();
    layerAssets.forEach((layer) => {
      if (isFillLayerByDefault(layer)) {
        autoFixed.add(layer.id);
      }
    });
    return autoFixed;
  }, [layerAssets, fixedLayerIds]);

  // Check if there are background layers (for showing background position control)
  const hasBackgroundLayers = useMemo(() => {
    return layerAssets.some(isBackgroundLayer);
  }, [layerAssets]);

  // Check if there are frame layers (for showing frame position control)
  const hasFrameLayers = useMemo(() => {
    return layerAssets.some(isFrameLayer);
  }, [layerAssets]);

  // Handle image load - update bounds for min scale calculation
  const handleImageLoad = useCallback(
    (width: number, height: number) => {
      setImageBounds({ width, height });
    },
    [setImageBounds]
  );

  const handleExport = async () => {
    if (!imageUrl && !useLayerMode) return;

    setIsExporting(true);

    try {
      const exportWidth = selectedPreset.width;
      const exportHeight = selectedPreset.height;

      // Create high-res export canvas
      const exportCanvas = document.createElement('canvas');
      exportCanvas.width = exportWidth;
      exportCanvas.height = exportHeight;
      const ctx = exportCanvas.getContext('2d');
      if (!ctx) {
        throw new Error('Could not get canvas context');
      }

      const scaleFactorX = exportWidth / viewportWidth;
      const scaleFactorY = exportHeight / viewportHeight;

      // Draw background if selected
      if (activeBackgroundUrl) {
        await new Promise<void>((resolve, reject) => {
          const bgImg = new Image();
          bgImg.crossOrigin = 'anonymous';
          bgImg.onload = () => {
            // Draw background with cover mode
            const imageAspect = bgImg.width / bgImg.height;
            const canvasAspect = exportWidth / exportHeight;

            let drawWidth: number;
            let drawHeight: number;

            if (imageAspect > canvasAspect) {
              drawHeight = exportHeight;
              drawWidth = drawHeight * imageAspect;
            } else {
              drawWidth = exportWidth;
              drawHeight = drawWidth / imageAspect;
            }

            const drawX = (exportWidth - drawWidth) / 2;
            // Apply vertical offset as percentage of overflow
            const verticalOverflow = drawHeight - exportHeight;
            const yOffset = (backgroundVerticalOffset / 100) * (verticalOverflow / 2);
            const drawY = (exportHeight - drawHeight) / 2 + yOffset;

            ctx.drawImage(bgImg, drawX, drawY, drawWidth, drawHeight);
            resolve();
          };
          bgImg.onerror = () => reject(new Error('Failed to load background image'));
          bgImg.src = activeBackgroundUrl;
        });
      }

      if (useLayerMode) {
        // Layer-based export
        await preloadLayerImages(layerAssets);

        for (const layer of layerAssets) {
          const img = getDecodedImageFromCache(layer.uri);
          if (!img) continue;

          ctx.save();

          const isFixed = effectiveFixedLayerIds.has(layer.id);
          ctx.imageSmoothingEnabled = isFixed;

          if (isFixed) {
            // Skip NFT's own background/frame layers if a custom background is selected
            if (activeBackgroundUrl) {
              ctx.restore();
              continue;
            }

            // Fill layers: cover the canvas
            const imageAspect = img.width / img.height;
            const canvasAspect = exportWidth / exportHeight;

            let drawWidth: number;
            let drawHeight: number;

            if (imageAspect > canvasAspect) {
              drawHeight = exportHeight;
              drawWidth = drawHeight * imageAspect;
            } else {
              drawWidth = exportWidth;
              drawHeight = drawWidth / imageAspect;
            }

            // Determine if this is a frame layer or background layer for vertical offset
            const verticalOffsetPercent = isFrameLayer(layer)
              ? frameVerticalOffset
              : isBackgroundLayer(layer)
                ? backgroundVerticalOffset
                : 0;

            // Apply vertical offset as percentage of overflow
            const verticalOverflow = drawHeight - exportHeight;
            const yOffset = (verticalOffsetPercent / 100) * (verticalOverflow / 2);

            const drawX = (exportWidth - drawWidth) / 2;
            const drawY = (exportHeight - drawHeight) / 2 + yOffset;

            ctx.drawImage(img, drawX, drawY, drawWidth, drawHeight);
          } else {
            // Standard layers: contain within height, transform with pan/zoom
            const imageAspect = img.width / img.height;

            // Base size: fit within canvas height (contain-height mode)
            let baseHeight = exportHeight;
            let baseWidth = baseHeight * imageAspect;

            // Apply user scale
            const drawWidth = baseWidth * scale;
            const drawHeight = baseHeight * scale;

            // Scale offset from viewport to export size
            const drawX = (exportWidth - drawWidth) / 2 + offset.x * scaleFactorX;
            const drawY = (exportHeight - drawHeight) / 2 + offset.y * scaleFactorY;

            ctx.drawImage(img, drawX, drawY, drawWidth, drawHeight);
          }

          ctx.restore();
        }
      } else {
        // Legacy single image export with contain-height mode
        const img = new Image();
        img.crossOrigin = 'anonymous';

        await new Promise<void>((resolve, reject) => {
          img.onload = () => {
            ctx.save();
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = 'high';

            const imageAspect = img.width / img.height;

            // Contain-height mode: fit within canvas height
            let baseHeight = exportHeight;
            let baseWidth = baseHeight * imageAspect;

            // Apply scale
            const drawWidth = baseWidth * scale;
            const drawHeight = baseHeight * scale;

            const drawX = (exportWidth - drawWidth) / 2 + offset.x * scaleFactorX;
            const drawY = (exportHeight - drawHeight) / 2 + offset.y * scaleFactorY;

            ctx.drawImage(img, drawX, drawY, drawWidth, drawHeight);
            ctx.restore();

            resolve();
          };
          img.onerror = () => reject(new Error('Failed to load image'));
        });

        img.src = imageUrl;
      }

      // Draw additional NFT characters (character layers only)
      if (additionalNFTs.length > 0) {
        const allAdditionalLayers = additionalNFTs.flatMap((nft) => nft.characterOnlyLayers);
        await preloadLayerImages(allAdditionalLayers);

        for (const additionalNFT of additionalNFTs) {
          for (const layer of additionalNFT.characterOnlyLayers) {
            const img = getDecodedImageFromCache(layer.uri);
            if (!img) continue;

            ctx.save();
            ctx.imageSmoothingEnabled = false;

            const imageAspect = img.width / img.height;

            // Base size: fit within canvas height (contain-height mode)
            let baseHeight = exportHeight;
            let baseWidth = baseHeight * imageAspect;

            // Apply the NFT's individual scale
            const drawWidth = baseWidth * additionalNFT.scale;
            const drawHeight = baseHeight * additionalNFT.scale;

            // Center on canvas and apply this NFT's individual offset
            const drawX = (exportWidth - drawWidth) / 2 + additionalNFT.offset.x * scaleFactorX;
            const drawY = (exportHeight - drawHeight) / 2 + additionalNFT.offset.y * scaleFactorY;

            ctx.drawImage(img, drawX, drawY, drawWidth, drawHeight);
            ctx.restore();
          }
        }
      }

      // Convert to PNG blob and download using smart download utility
      const blob = await new Promise<Blob | null>((resolve) => {
        exportCanvas.toBlob((b) => resolve(b), 'image/png');
      });

      if (blob) {
        const nftName = currentNFT?.name || 'banner';
        const filename = `${nftName.replace(/\s+/g, '_')}_banner_${exportWidth}x${exportHeight}.png`;

        const result = await downloadImage(blob, filename, {
          onPreviewRequired: (dataUrl, fname) => {
            setPreviewImage({ url: dataUrl, filename: fname });
          },
        });

        // Only close modal if download succeeded and wasn't a preview fallback
        if (result.success && result.method !== 'preview') {
          closeModal();
        }
      }
    } catch (error) {
      console.error('Failed to export banner:', error);
      alert('Failed to export banner. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const handleClose = () => {
    reset();
    setAdditionalNFTs([]);
    setSelectedNFTId(null);
    setShowNFTSearch(false);
    setNftSearchValue('');
    setPreviewNFT(null);
    setSearchError(null);
    closeModal();
  };

  const handleZoomChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setScale(parseFloat(e.target.value));
  };

  const zoomIn = () => {
    setScale(Math.min(3, scale + 0.1));
  };

  const zoomOut = () => {
    setScale(Math.max(minScale, scale - 0.1));
  };

  // Background handlers
  const handleBackgroundSelect = (preset: BackgroundPreset) => {
    if (preset.isUpload) {
      backgroundInputRef.current?.click();
    } else {
      setSelectedBackground(preset);
      setCustomBackgroundUrl(null);
    }
  };

  const handleBackgroundUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const dataUrl = event.target?.result as string;
      setCustomBackgroundUrl(dataUrl);
      // Set to upload preset
      const uploadPreset = BACKGROUND_PRESETS.find(p => p.isUpload);
      if (uploadPreset) {
        setSelectedBackground(uploadPreset);
      }
    };
    reader.readAsDataURL(file);
  };

  // NFT Search handlers
  const handleNFTSearch = async (e: React.FormEvent) => {
    e.preventDefault();

    const nftNumber = parseInt(nftSearchValue);
    if (isNaN(nftNumber) || nftNumber <= 0) {
      setSearchError('Please enter a valid NFT number');
      return;
    }

    setIsSearching(true);
    setSearchError(null);
    setPreviewNFT(null);

    // Check cache first
    const cached = fetchedNFTs.get(nftNumber);
    if (cached) {
      setPreviewNFT(cached);
      setIsSearching(false);
      return;
    }

    const result = await fetchNFTByNumber(nftNumber);

    if (result.success && result.data) {
      setPreviewNFT(result.data);
    } else {
      setSearchError(result.error || 'Failed to fetch NFT');
    }

    setIsSearching(false);
  };

  const handleAddNFTToCanvas = () => {
    if (!previewNFT) return;

    const characterLayers = getCharacterOnlyLayers(previewNFT.layerAssets);

    const newElement: BannerCropNFTElement = {
      id: `nft-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      nftData: previewNFT,
      offset: { x: 0, y: 0 },
      scale: 1,
      characterOnlyLayers: characterLayers,
    };

    setAdditionalNFTs([...additionalNFTs, newElement]);
    setShowNFTSearch(false);
    setNftSearchValue('');
    setPreviewNFT(null);
    setSearchError(null);
    setSelectedNFTId(newElement.id);
  };

  const handleCancelNFTSearch = () => {
    setShowNFTSearch(false);
    setNftSearchValue('');
    setPreviewNFT(null);
    setSearchError(null);
  };

  const handleRemoveAdditionalNFT = (id: string) => {
    setAdditionalNFTs(additionalNFTs.filter((nft) => nft.id !== id));
    if (selectedNFTId === id) {
      setSelectedNFTId(null);
    }
  };

  const handleUpdateAdditionalNFTOffset = (id: string, newOffset: { x: number; y: number }) => {
    setAdditionalNFTs(
      additionalNFTs.map((nft) =>
        nft.id === id ? { ...nft, offset: newOffset } : nft
      )
    );
  };

  const handleUpdateAdditionalNFTScale = (id: string, newScale: number) => {
    setAdditionalNFTs(
      additionalNFTs.map((nft) =>
        nft.id === id ? { ...nft, scale: newScale } : nft
      )
    );
  };

  // Get the actual background URL to use
  const activeBackgroundUrl = customBackgroundUrl || selectedBackground.url;

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Create Banner"
      size="xl"
      footer={
        <>
          <Button variant="ghost" onClick={handleClose} disabled={isExporting}>
            Cancel
          </Button>
          <Button
            variant="game3d"
            onClick={handleExport}
            disabled={(!imageUrl && !useLayerMode) || isExporting}
            isLoading={isExporting}
          >
            Export Banner
          </Button>
        </>
      }
    >
      <div className="space-y-6">
        {/* Hidden file input for background upload */}
        <input
          ref={backgroundInputRef}
          type="file"
          accept="image/*"
          onChange={handleBackgroundUpload}
          className="hidden"
        />

        {/* Background Selection */}
        <div className="space-y-2 overflow-hidden">
          <label className="text-sm font-medium text-text-dark dark:text-slate-200">Background:</label>
          <div className="flex flex-wrap gap-2 justify-center max-w-full overflow-hidden">
            {BACKGROUND_PRESETS.map((preset) => (
              <button
                key={preset.id}
                onClick={() => handleBackgroundSelect(preset)}
                className={`relative w-16 h-16 flex-shrink-0 rounded-lg border-2 overflow-hidden transition-all ${
                  selectedBackground.id === preset.id
                    ? 'border-game-blue ring-2 ring-game-blue/50'
                    : 'border-surface-border dark:border-slate-600 hover:border-game-blue/50'
                }`}
                title={preset.name}
              >
                {preset.id === 'none' ? (
                  <div className="w-full h-full bg-surface-secondary dark:bg-slate-800 flex items-center justify-center">
                    <svg className="w-6 h-6 text-text-muted dark:text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                    </svg>
                  </div>
                ) : preset.isUpload ? (
                  <div className="w-full h-full bg-surface-secondary dark:bg-slate-800 flex flex-col items-center justify-center gap-1">
                    {customBackgroundUrl ? (
                      <img src={customBackgroundUrl} alt="Custom" className="w-full h-full object-cover" />
                    ) : (
                      <>
                        <svg className="w-5 h-5 text-text-muted dark:text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <span className="text-[10px] text-text-muted dark:text-slate-400">Upload</span>
                      </>
                    )}
                  </div>
                ) : (
                  <img src={preset.thumbnail} alt={preset.name} className="w-full h-full object-cover" />
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Vertical Position Controls for Background and Frame */}
        {(activeBackgroundUrl || hasBackgroundLayers || hasFrameLayers) && (
          <div className="space-y-3">
            {/* Background Vertical Position - show for custom background OR NFT's own background layers */}
            {(activeBackgroundUrl || hasBackgroundLayers) && (
              <div className="flex items-center gap-3">
                <label className="text-sm font-medium text-text-dark dark:text-slate-200 w-32 flex-shrink-0">Background:</label>
                <input
                  type="range"
                  min="-100"
                  max="100"
                  step="1"
                  value={backgroundVerticalOffset}
                  onChange={(e) => setBackgroundVerticalOffset(parseInt(e.target.value))}
                  className="flex-1 h-2 bg-surface-secondary dark:bg-slate-700 rounded-lg appearance-none cursor-pointer accent-game-blue"
                />
                <span className="text-xs text-text-muted dark:text-slate-400 w-10 text-right">{backgroundVerticalOffset}%</span>
              </div>
            )}

            {/* Frame Vertical Position - show when NFT has frame layers and no custom background */}
            {hasFrameLayers && !activeBackgroundUrl && (
              <div className="flex items-center gap-3">
                <label className="text-sm font-medium text-text-dark dark:text-slate-200 w-32 flex-shrink-0">Frame:</label>
                <input
                  type="range"
                  min="-100"
                  max="100"
                  step="1"
                  value={frameVerticalOffset}
                  onChange={(e) => setFrameVerticalOffset(parseInt(e.target.value))}
                  className="flex-1 h-2 bg-surface-secondary dark:bg-slate-700 rounded-lg appearance-none cursor-pointer accent-game-blue"
                />
                <span className="text-xs text-text-muted dark:text-slate-400 w-10 text-right">{frameVerticalOffset}%</span>
              </div>
            )}
          </div>
        )}

        {/* Canvas Preview */}
        <div className="flex flex-col items-center gap-4">
          <div
            ref={containerRef}
            className={`relative ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
            style={{ touchAction: 'none' }}
          >
            <BannerCropCanvas
              width={selectedPreset.width}
              height={selectedPreset.height}
              imageUrl={!useLayerMode ? imageUrl : undefined}
              layerAssets={useLayerMode ? layerAssets : undefined}
              offset={offset}
              scale={scale}
              onImageLoad={handleImageLoad}
              fixedLayerIds={useLayerMode ? effectiveFixedLayerIds : undefined}
              viewportWidth={viewportWidth}
              viewportHeight={viewportHeight}
              backgroundUrl={activeBackgroundUrl}
              backgroundVerticalOffset={backgroundVerticalOffset}
              frameVerticalOffset={frameVerticalOffset}
              snapGuides={snapGuides}
              isDragging={isDragging}
              additionalNFTs={additionalNFTs}
              selectedNFTId={selectedNFTId}
              onDragAdditionalNFT={handleUpdateAdditionalNFTOffset}
              snapEnabled={snapEnabled}
              snapThreshold={12}
              onSelectNFT={setSelectedNFTId}
            />
          </div>

          {!imageUrl && !useLayerMode && (
            <p className="text-sm text-text-muted dark:text-slate-400 italic">
              No NFT loaded. Search for an NFT to create a banner.
            </p>
          )}
        </div>

        {/* Add NFT Characters Section */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-text-dark dark:text-slate-200">NFT Characters</label>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setShowNFTSearch(true)}
            >
              <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add NFT
            </Button>
          </div>

          {/* NFT Search Panel */}
          {showNFTSearch && (
            <div className="bg-surface-secondary dark:bg-slate-800 rounded-lg p-4 border border-surface-border dark:border-slate-600">
              <form onSubmit={handleNFTSearch} className="space-y-3">
                <Input
                  label="NFT Number"
                  type="number"
                  placeholder="Enter NFT # (e.g., 42)"
                  value={nftSearchValue}
                  onChange={(e) => setNftSearchValue(e.target.value)}
                  error={searchError}
                  min={1}
                />
                <div className="flex gap-2">
                  <Button
                    type="submit"
                    variant="primary"
                    size="sm"
                    isLoading={isSearching}
                    className="flex-1"
                  >
                    Search
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handleCancelNFTSearch}
                  >
                    Cancel
                  </Button>
                </div>
              </form>

              {/* Preview NFT */}
              {previewNFT && (
                <div className="mt-4 p-3 bg-surface-primary dark:bg-slate-700 rounded-lg border border-surface-border dark:border-slate-600">
                  <div className="flex gap-3 items-center">
                    <img
                      src={previewNFT.imageUrl}
                      alt={previewNFT.name}
                      className="w-16 h-16 rounded-lg object-cover"
                    />
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-text-dark dark:text-slate-100 text-sm truncate">{previewNFT.name}</h4>
                      <p className="text-xs text-text-muted dark:text-slate-400 mt-0.5">
                        {getCharacterOnlyLayers(previewNFT.layerAssets).length} character layer(s)
                      </p>
                    </div>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={handleAddNFTToCanvas}
                    >
                      Add
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* List of All NFT Characters */}
          <div className="space-y-2">
            {/* Primary NFT (the main one loaded) */}
            {currentNFT && (
              <div
                className={`flex items-center gap-3 p-2 rounded-lg border transition-all cursor-pointer ${
                  selectedNFTId === PRIMARY_NFT_ID
                    ? 'border-game-blue bg-game-blue/10'
                    : 'border-surface-border dark:border-slate-600 bg-surface-secondary dark:bg-slate-800 hover:border-game-blue/50'
                }`}
                onClick={() => setSelectedNFTId(selectedNFTId === PRIMARY_NFT_ID ? null : PRIMARY_NFT_ID)}
              >
                <img
                  src={currentNFT.imageUrl}
                  alt={currentNFT.name}
                  className="w-10 h-10 rounded object-cover"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-text-dark dark:text-slate-200 truncate">{currentNFT.name}</p>
                  <p className="text-xs text-game-blue">Primary</p>
                </div>
              </div>
            )}

            {/* Additional NFTs */}
            {additionalNFTs.map((nft) => (
              <div
                key={nft.id}
                className={`flex items-center gap-3 p-2 rounded-lg border transition-all cursor-pointer ${
                  selectedNFTId === nft.id
                    ? 'border-game-blue bg-game-blue/10'
                    : 'border-surface-border dark:border-slate-600 bg-surface-secondary dark:bg-slate-800 hover:border-game-blue/50'
                }`}
                onClick={() => setSelectedNFTId(selectedNFTId === nft.id ? null : nft.id)}
              >
                <img
                  src={nft.nftData.imageUrl}
                  alt={nft.nftData.name}
                  className="w-10 h-10 rounded object-cover"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-text-dark dark:text-slate-200 truncate">{nft.nftData.name}</p>
                  <p className="text-xs text-text-muted dark:text-slate-400">Character only</p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveAdditionalNFT(nft.id);
                  }}
                  aria-label="Remove NFT"
                >
                  <svg className="w-4 h-4 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </Button>
              </div>
            ))}
          </div>

          {/* Selected NFT Controls */}
          {selectedNFTId && (
            <div className="bg-surface-secondary dark:bg-slate-800 rounded-lg p-3 border border-surface-border dark:border-slate-600 space-y-3">
              {selectedNFTId === PRIMARY_NFT_ID ? (
                /* Primary NFT Controls - Zoom and Position */
                <>
                  <p className="text-xs text-text-muted dark:text-slate-400">
                    Drag the character on the canvas to reposition.
                  </p>

                  {/* Zoom Control for Primary NFT */}
                  <div className="flex items-center gap-3">
                    <label className="text-sm font-medium text-text-dark dark:text-slate-200 w-16 flex-shrink-0">Zoom:</label>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={zoomOut}
                      disabled={scale <= minScale}
                      aria-label="Zoom Out"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM13 10H7" />
                      </svg>
                    </Button>
                    <input
                      type="range"
                      min={minScale}
                      max="3"
                      step="0.02"
                      value={scale}
                      onChange={handleZoomChange}
                      className="flex-1 h-2 bg-surface-primary dark:bg-slate-700 rounded-lg appearance-none cursor-pointer accent-game-blue"
                    />
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={zoomIn}
                      disabled={scale >= 3}
                      aria-label="Zoom In"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                      </svg>
                    </Button>
                    <span className="text-xs text-text-muted dark:text-slate-400 w-10 text-right">{Math.round(scale * 100)}%</span>
                  </div>

                  {/* Reset Button */}
                  <div className="flex justify-end">
                    <Button variant="ghost" size="sm" onClick={reset}>
                      <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      Reset Position
                    </Button>
                  </div>
                </>
              ) : (
                /* Additional NFT Controls - Scale only (position via canvas drag) */
                <>
                  <p className="text-xs text-text-muted dark:text-slate-400">
                    Drag the character on the canvas to reposition.
                  </p>

                  {/* Scale Control */}
                  <div className="flex items-center gap-3">
                    <label className="text-sm font-medium text-text-dark dark:text-slate-200 w-16 flex-shrink-0">Scale:</label>
                    <input
                      type="range"
                      min="0.2"
                      max="3"
                      step="0.1"
                      value={additionalNFTs.find((n) => n.id === selectedNFTId)?.scale || 1}
                      onChange={(e) => handleUpdateAdditionalNFTScale(selectedNFTId, parseFloat(e.target.value))}
                      className="flex-1 h-2 bg-surface-primary dark:bg-slate-700 rounded-lg appearance-none cursor-pointer accent-game-blue"
                    />
                    <span className="text-xs text-text-muted dark:text-slate-400 w-10 text-right">
                      {Math.round((additionalNFTs.find((n) => n.id === selectedNFTId)?.scale || 1) * 100)}%
                    </span>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Settings */}
        <div className="space-y-3">
          <div className="flex items-center justify-center gap-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={snapEnabled}
                onChange={(e) => setSnapEnabled(e.target.checked)}
                className="w-4 h-4 rounded border-surface-border dark:border-slate-600 text-game-blue focus:ring-game-blue"
              />
              <span className="text-sm text-text-dark dark:text-slate-200">Snap to center</span>
            </label>
          </div>

          <p className="text-xs text-text-muted dark:text-slate-400 text-center">
            Click and drag characters directly on the canvas to move them.
          </p>
        </div>
      </div>

      {/* Image Preview Modal for Telegram fallback */}
      <ImagePreviewModal
        isOpen={!!previewImage}
        onClose={() => {
          setPreviewImage(null);
          closeModal();
        }}
        imageUrl={previewImage?.url || ''}
        filename={previewImage?.filename || ''}
      />
    </Modal>
  );
}
